import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { z } from 'zod';
import { useRuntime } from './context';
import { useHasModule, useRemote } from './useRemote';
import { useLocale, useT } from '../messages';
import { formatFreshness } from '../mirror/time';
import { useNow } from './context';
import { putWithProgress } from './upload';
import {
  AlbumCodeIssued,
  AlbumCreated,
  AlbumDetail,
  AlbumFinalized,
  AlbumList,
  AlbumUploadUrls,
} from '../wire';
import type { Album, AlbumUploadUrls as Uploads } from '../wire';

/**
 * **The studio** — `docs/tour-photos-design.md` decision 5.
 *
 * > Albums screen behind the `photos` module: create (optionally from a
 * > departure instance — the captain's day links straight to "today's sailing's
 * > album"), upload with progress, the code shown ONCE at creation (the CLI
 * > generate-password precedent), regenerate = revoke + new. Offline boundary
 * > honest: uploads are online-only and say so.
 *
 * ## Online-only, and it says so rather than pretending
 *
 * `Orders`, `Departures` and `FrontDesk` are online-only because their state is
 * a race with other people. This screen is online-only for a blunter reason:
 * **there is nowhere to queue a photograph.** The outbox holds JSON intents in
 * IndexedDB and drains them as HTTP requests; a hundred and eighty JPEGs is not
 * an intent, it is two gigabytes, and the upload does not go through this
 * service at all — it is a signed PUT straight to the bucket, against a URL that
 * expires in an hour. An outbox entry holding a stale signed URL would be a
 * queue of guaranteed failures.
 *
 * So the screen says so, standing, above the control — and it does **not**
 * disable anything on `useConnection()`. That was the first draft and it was
 * wrong: `connection` is the state of the *SSE stream*, not of the network, and
 * a stream that blipped while the tunnel is fine would have refused a
 * photographer an album on a working connection. What a device honestly knows is
 * what happened when it tried, so the control is always offered, the boundary is
 * always stated, and a failure prints the server's own sentence. §8.1's rule is
 * that a device says what it knows and when it knew it; this is that rule
 * applied to what it *cannot* do.
 *
 * Album metadata is read with `useRemote` like the other online screens rather
 * than mirrored — a photographer's album list is not the book, and mirroring it
 * would mean a schema change and a sync path for a screen that cannot work
 * offline anyway.
 *
 * ## The code is shown once, and the screen says so
 *
 * The CLI's `keys issue` precedent, and it has a cost this screen has to be
 * honest about: a photographer who dismisses the panel has lost the code and
 * must regenerate. That sentence is printed *beside the code*, at the moment it
 * can still be acted on, rather than discovered later in a support call.
 *
 * `navigator.clipboard` is offered and the code is **also shown in full**, for
 * `PaymentLinks.tsx`'s reason: on an installed PWA behind a permission prompt
 * the clipboard is often unavailable, and a code that can only be copied is a
 * code that cannot be read onto a card.
 *
 * ## `XMLHttpRequest`, on purpose (`upload.ts`)
 *
 * The only `XMLHttpRequest` in this bundle. `fetch` has no upload progress
 * event, and a hundred-and-eighty-file upload over a dock's wifi with no
 * progress is indistinguishable from a hung screen — which is the state a
 * photographer force-quits out of, losing the batch. `ApiClient` cannot carry it
 * either: it JSON-stringifies every body and zod-parses every response, and this
 * request sends image bytes to a different origin and expects none.
 */

const MAX_BATCH = 50;

type UploadState = 'waiting' | 'uploading' | 'done' | 'error';

interface FileProgress {
  name: string;
  state: UploadState;
  /** 0–100. `null` while the browser has not reported any yet. */
  percent: number | null;
}

export function Albums(): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const now = useNow();
  const entitled = useHasModule('photos');
  const [selected, setSelected] = useState<string | null>(null);

  const albums = useRemote(
    (signal) => runtime.api.request({ method: 'GET', path: '/albums', schema: AlbumList, signal }),
    [],
    { cue: (type) => type.startsWith('album.'), pollMs: 30_000, enabled: entitled },
  );

  /** Hidden by `App`; silent here. See `Orders.tsx` for why it is checked twice. */
  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('albums.notSwitchedOn')}</p>
      </section>
    );
  }

  const rows = albums.data?.albums ?? [];

  return (
    <section className="view view--albums">
      <div className="view__header">
        <h1>{t('albums.title')}</h1>
        <span className="status-strip__staleness">
          {formatFreshness(albums.fetchedAt, now, 'UTC', locale)}
          {albums.error === null ? '' : t('albums.couldNotReach')}
        </span>
      </div>

      <NewAlbum onCreated={() => albums.reload()} />

      {albums.loading && albums.data === null ? (
        <p className="empty">{t('albums.reading')}</p>
      ) : null}
      {rows.length === 0 && !albums.loading ? <p className="empty">{t('albums.none')}</p> : null}

      <ul className="day-list album-list">
        {rows.map((album) => (
          <AlbumRow key={album.album_id} album={album} onOpen={() => setSelected(album.album_id)} />
        ))}
      </ul>

      {selected === null ? null : (
        <AlbumSheet
          albumId={selected}
          onClose={() => {
            setSelected(null);
            albums.reload();
          }}
        />
      )}
    </section>
  );
}

function AlbumRow({ album, onOpen }: { album: Album; onOpen: () => void }): ReactNode {
  const t = useT();
  const locale = useLocale();
  const expires = new Date(album.expires_at);

  return (
    <li className={`day-row ${album.status === 'revoked' ? 'is-retired' : ''}`}>
      <div className="day-row__who">
        <strong>{album.title === '' ? t('albums.untitled') : album.title}</strong>
        <small className="muted">
          {t('albums.photoCount', { count: album.photo_count })}
          {album.departure_instance_id === null ? '' : t('albums.fromSailing')}
          {album.status === 'revoked' ? t('albums.takenDown') : ''}
        </small>
      </div>
      <div className="day-row__tail">
        <small className="muted">
          {t('albums.expires', { date: expires.toLocaleDateString(locale) })}
        </small>
        <div className="row-actions">
          <button type="button" className="link-button" onClick={onOpen}>
            {t('common.open')}
          </button>
        </div>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Create                                                              */
/* ------------------------------------------------------------------ */

/**
 * Create an album, optionally anchored to a sailing.
 *
 * `departureInstanceId` is a prop rather than a field the photographer types,
 * because the only caller that has one is the Departures screen — "today's
 * sailing's album", decision 5's own example. Typing a `di_` id into a text box
 * is not a thing anybody would do.
 */
export function NewAlbum({
  departureInstanceId,
  onCreated,
}: {
  departureInstanceId?: string;
  onCreated?: (album: AlbumCreated) => void;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [issued, setIssued] = useState<AlbumCreated | null>(null);

  const create = useCallback(async (): Promise<void> => {
    setBusy(true);
    setFailure(null);
    try {
      const album = await runtime.api.request({
        method: 'POST',
        path: '/albums',
        body: {
          ...(title.trim() === '' ? {} : { title: title.trim() }),
          ...(departureInstanceId === undefined
            ? {}
            : { departure_instance_id: departureInstanceId }),
        },
        idempotencyKey: ulid(),
        schema: AlbumCreated,
      });
      setTitle('');
      setIssued(album);
      onCreated?.(album);
    } catch (e) {
      // The server's sentence about this exact album, verbatim — including the
      // `503` that names `PHOTOS_BUCKET` when a deployment stores no photos.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [runtime, title, departureInstanceId, onCreated]);

  return (
    <div className="editor editor--inline album-new">
      <label className="field">
        <span>{t('albums.titleLabel')}</span>
        <input
          type="text"
          value={title}
          placeholder={t('albums.titlePlaceholder')}
          onChange={(e) => setTitle(e.target.value)}
          aria-label={t('albums.titleLabel')}
        />
      </label>
      <div className="sheet__actions">
        <button
          type="button"
          className="button button--primary"
          disabled={busy}
          onClick={() => void create()}
        >
          {busy ? t('albums.creating') : t('albums.create')}
        </button>
      </div>
      {failure === null ? null : (
        <p className="error" role="alert">
          {failure}
        </p>
      )}

      {issued === null ? null : <CodeOnce code={issued.code} onClose={() => setIssued(null)} />}
    </div>
  );
}

/**
 * The code, shown once.
 *
 * Shown **and** copyable, and the sentence about it being the only time is
 * beside it rather than in a tooltip. A photographer reads this off a screen
 * onto a card with a pen; the clipboard is the fast path and the visible
 * characters are the one that works.
 */
function CodeOnce({ code, onClose }: { code: string; onClose: () => void }): ReactNode {
  const t = useT();
  const [copied, setCopied] = useState(false);

  return (
    <div className="sheet" role="dialog" aria-label={t('albums.codeDialog')}>
      <div className="sheet__panel">
        <h2>{t('albums.codeTitle')}</h2>
        <p className="album-code">{code}</p>
        <p className="note">{t('albums.codeOnce')}</p>
        <div className="sheet__actions">
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setCopied(true);
              void navigator.clipboard?.writeText(code).catch(() => undefined);
            }}
          >
            {copied ? t('albums.copied') : t('albums.copy')}
          </button>
          <button type="button" className="button button--primary" onClick={onClose}>
            {t('albums.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* One album                                                           */
/* ------------------------------------------------------------------ */

export function AlbumSheet({
  albumId,
  onClose,
}: {
  albumId: string;
  onClose: () => void;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const [progress, setProgress] = useState<FileProgress[]>([]);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const detail = useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: `/albums/${albumId}`,
        schema: AlbumDetail,
        signal,
      }),
    [albumId],
    { cue: (type) => type.startsWith('album.'), pollMs: 0 },
  );

  /**
   * Mint, upload, finalize — the three steps of decision 4, in that order.
   *
   * The order is what makes a failure cheap. Nothing is recorded until the bytes
   * have landed, so a phone that dies mid-batch leaves objects with no rows —
   * invisible, and swept by the bucket's lifecycle rule — rather than rows with
   * no objects, which is a broken tile in a family's album forever.
   *
   * Uploads run **one at a time**, like the reference implementation's. Parallel
   * would be faster on a hotel wifi and worse on the one that matters: a dock's
   * uplink saturates, every request slows together, and the progress bar stops
   * meaning anything. Sequential also makes "which file failed" a fact rather
   * than a guess.
   */
  const upload = useCallback(
    async (files: FileList): Promise<void> => {
      const chosen = [...files].slice(0, MAX_BATCH);
      if (chosen.length === 0) return;

      setBusy(true);
      setFailure(null);
      setProgress(chosen.map((f) => ({ name: f.name, state: 'waiting', percent: null })));

      const mark = (index: number, patch: Partial<FileProgress>): void => {
        setProgress((current) =>
          current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
        );
      };

      try {
        const minted: Uploads = await runtime.api.request({
          method: 'POST',
          path: `/albums/${albumId}/upload-urls`,
          body: {
            files: chosen.map((f) => ({
              content_type: contentTypeOf(f),
              filename: f.name,
            })),
          },
          schema: AlbumUploadUrls,
        });

        const landed: Array<{
          media_id: string;
          content_type: string;
          size_bytes: number;
          filename: string;
        }> = [];

        for (const [index, file] of chosen.entries()) {
          const target = minted.uploads[index];
          if (target === undefined) continue;
          mark(index, { state: 'uploading', percent: 0 });
          try {
            await putWithProgress(target.url, file, contentTypeOf(file), (percent) => {
              mark(index, { percent });
            });
            mark(index, { state: 'done', percent: 100 });
            landed.push({
              media_id: target.media_id,
              content_type: contentTypeOf(file),
              size_bytes: file.size,
              filename: file.name,
            });
          } catch {
            // One file's failure is one file's. The loop continues, which is the
            // whole reason the finalize call takes a list of successes.
            mark(index, { state: 'error', percent: null });
          }
        }

        if (landed.length > 0) {
          const finalized = await runtime.api.request({
            method: 'POST',
            path: `/albums/${albumId}/media`,
            body: { files: landed },
            idempotencyKey: ulid(),
            schema: AlbumFinalized,
          });
          for (const refusal of finalized.refused) {
            const at = landed.findIndex((l) => l.media_id === refusal.media_id);
            if (at >= 0) mark(at, { state: 'error', percent: null });
          }
        }
        detail.reload();
      } catch (e) {
        setFailure(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
        if (inputRef.current !== null) inputRef.current.value = '';
      }
    },
    [runtime, albumId, detail],
  );

  const regenerate = useCallback(async (): Promise<void> => {
    setFailure(null);
    try {
      const answer = await runtime.api.request({
        method: 'POST',
        path: `/albums/${albumId}/code`,
        body: {},
        idempotencyKey: ulid(),
        schema: AlbumCodeIssued,
      });
      setIssued(answer.code);
      detail.reload();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    }
  }, [runtime, albumId, detail]);

  const album = detail.data;

  // "Text the family" (docs/tour-photos-design.md, the notification gap closed):
  // the number typed here, or the album's client when the field is empty. The
  // server builds the link and refuses by name when it has nobody to text.
  const [notifyPhone, setNotifyPhone] = useState('');
  const [notified, setNotified] = useState<string | null>(null);
  const [notifying, setNotifying] = useState(false);
  const notify = useCallback(async (): Promise<void> => {
    setFailure(null);
    setNotified(null);
    setNotifying(true);
    try {
      const answer = await runtime.api.request({
        method: 'POST',
        path: `/albums/${albumId}/notify`,
        body: notifyPhone.trim() === '' ? {} : { phone: notifyPhone.trim() },
        idempotencyKey: ulid(),
        schema: z.object({ sent_to: z.string(), link: z.string() }),
      });
      setNotified(answer.sent_to);
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setNotifying(false);
    }
  }, [runtime, albumId, notifyPhone]);

  return (
    <div className="sheet" role="dialog" aria-label={t('albums.dialog')}>
      <div className="sheet__panel sheet__panel--wide">
        <h2>{album === null ? t('albums.title') : album.title || t('albums.untitled')}</h2>
        {album === null ? null : (
          <p className="muted">{t('albums.photoCount', { count: album.photo_count })}</p>
        )}

        {failure === null ? null : (
          <p className="error" role="alert">
            {failure}
          </p>
        )}

        {/*
          The boundary, said standing rather than by withdrawing a control. See
          the note at the top of this file: `connection` describes the event
          stream, and refusing an upload because the stream blipped would refuse
          it on a working tunnel.
        */}
        <p className="note">{t('albums.uploadsNeedConnection')}</p>
        <label className="album-drop">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={(e) => {
              if (e.target.files !== null) void upload(e.target.files);
            }}
            aria-label={t('albums.addPhotos')}
          />
          <span>{t('albums.addPhotos')}</span>
          <small className="muted">{t('albums.addPhotosHint', { max: MAX_BATCH })}</small>
        </label>

        {progress.length === 0 ? null : (
          <ul className="upload-list">
            {progress.map((row, i) => (
              <li key={`${row.name}-${String(i)}`} className={`upload upload--${row.state}`}>
                <span className="upload__name">{row.name}</span>
                <span className="upload__state">
                  {row.state === 'done'
                    ? t('albums.uploadDone')
                    : row.state === 'error'
                      ? t('albums.uploadFailed')
                      : row.percent === null
                        ? t('albums.uploadWaiting')
                        : t('albums.uploadPercent', { percent: row.percent })}
                </span>
              </li>
            ))}
          </ul>
        )}

        <ul className="album-grid">
          {(album?.media ?? []).map((media) => (
            <li key={media.media_id} className="album-tile">
              {/*
                The thumbnail: a fifteen-minute signed GET the album read carries
                (core-api mints it with its own grant, the same way the print
                composer reads). A plain <img> — the bucket's CORS rule admits
                this origin — and no thumbnail at all where a deployment stores
                no photographs, in which case the filename does the job it did
                before.
              */}
              {media.view === undefined ? null : (
                <img className="album-tile__thumb" src={media.view} alt="" loading="lazy" />
              )}
              <span className="album-tile__name">
                {media.filename === '' ? media.media_id : media.filename}
              </span>
              <button
                type="button"
                className="link-button link-button--danger"
                onClick={() => {
                  void runtime.api
                    .request({
                      method: 'DELETE',
                      path: `/albums/${albumId}/media/${media.media_id}`,
                      schema: AlbumDetail.pick({ album_id: true }).passthrough(),
                    })
                    .then(() => {
                      detail.reload();
                    })
                    .catch((e: unknown) => {
                      setFailure(e instanceof Error ? e.message : String(e));
                    });
                }}
              >
                {t('common.remove')}
              </button>
            </li>
          ))}
        </ul>

        <div className="album-notify">
          <label className="field">
            <span>{t('albums.notifyPhone')}</span>
            <input
              type="tel"
              value={notifyPhone}
              placeholder={t('albums.notifyPhonePlaceholder')}
              onChange={(e) => setNotifyPhone(e.target.value)}
              aria-label={t('albums.notifyPhone')}
            />
          </label>
          <button
            type="button"
            className="button button--primary"
            disabled={busy || notifying || (album?.photo_count ?? 0) === 0}
            onClick={() => void notify()}
          >
            {t('albums.notify')}
          </button>
          {notified === null ? null : (
            <p className="note" role="status">
              {t('albums.notified', { phone: notified })}
            </p>
          )}
        </div>

        <div className="sheet__actions">
          <button
            type="button"
            className="link-button"
            disabled={busy}
            onClick={() => void regenerate()}
          >
            {t('albums.regenerate')}
          </button>
          <button type="button" className="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
        <p className="note">{t('albums.regenerateHint')}</p>

        {issued === null ? null : <CodeOnce code={issued} onClose={() => setIssued(null)} />}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The upload itself                                                   */
/* ------------------------------------------------------------------ */

/**
 * The browser's reported type, narrowed to the closed set the signature is bound
 * to.
 *
 * A browser that reports nothing (which happens for `.heic` on some Androids)
 * gets `image/jpeg`, and the server refuses the PUT if the bytes disagree —
 * which is the right place for that refusal, since the signature is what
 * actually enforces it.
 */
function contentTypeOf(file: File): string {
  const known = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
  return known.includes(file.type) ? file.type : 'image/jpeg';
}
